import type { ReactNode } from 'react';

import { createContext, use, useState } from 'react';

/**
 * Context type for NotificationsSheet.
 * @property {boolean} open - Whether the sheet is open.
 * @property {(open: boolean) => void} onOpenChange - Callback to change open state.
 */
interface NotificationsSheetContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Props for NotificationsSheetProvider.
 * @property {ReactNode} children - Child components.
 */
interface NotificationsSheetProviderProps {
  children: ReactNode;
}

/**
 * Context for notifications sheet open state and handler.
 */
const NotificationsSheetContext = createContext<NotificationsSheetContextType | null>(null);

export function useNotificationsSheet() {
  const context = use(NotificationsSheetContext);
  if (context === null) {
    throw new Error('useNotificationsSheet must be used within a NotificationsSheetProvider');
  }
  return context;
}

export default function NotificationsSheetProvider(notificationsSheetProviderProps: NotificationsSheetProviderProps) {
  const { children } = notificationsSheetProviderProps;
  const [open, setOpen] = useState<boolean>(false);

  const onOpenChange = (open: boolean) => {
    setOpen(open);
  };

  return <NotificationsSheetContext value={{ open, onOpenChange }}>{children}</NotificationsSheetContext>;
}
